import { gql } from '@apollo/client';
import { GridItem, Stack } from '@chakra-ui/react';
import { Permission } from '@tp-vision/roles-permissions';
import { useTranslation } from 'react-i18next';
import { useAuth } from '~auth/useAuth';
import { ConfigurationStatusType } from '~components/configurations/ConfigStateToggle';
import { checkStateToDisableTab } from '../ConfigDetailSettings';
import { ConfigDisplayLockingSection_ConfigFragment } from './__generated__/ConfigDisplayLockingSection.graphql';
import { ConfigDisplayInfraRedControlToggle } from './ConfigDisplayInfraRedControlToggle';
import { ConfigDisplayKeyboardControlToggle } from './ConfigDisplayKeyboardControlToggle';
import { ConfigDisplayPortsControlToggle } from './ConfigDisplayPortsControlToggle';

interface Props {
  config: ConfigDisplayLockingSection_ConfigFragment;
  configStatus?: ConfigurationStatusType;
}

export function ConfigDisplayLockingSection({ config, configStatus }: Props) {
  const { verifyUserPermissions } = useAuth();
  const { t } = useTranslation();
  const isSupported =
    !!config.keyboardControl?.reported ||
    !!config.infraRedControl?.reported ||
    !!config.portsControl?.reported;

  const isDisplaySettingsUpdateDisabled =
    checkStateToDisableTab(configStatus) ||
    !verifyUserPermissions([Permission.ConfigurationUpdate]);

  return (
    <GridItem colSpan={{ base: 8, md: 7 }}>
      {isSupported ? (
        <Stack direction="column" spacing="10">
          <Stack direction="column" spacing="10">
            <ConfigDisplayInfraRedControlToggle
              config={config}
              isDisabled={isDisplaySettingsUpdateDisabled}
            />
            <ConfigDisplayKeyboardControlToggle
              config={config}
              isDisabled={isDisplaySettingsUpdateDisabled}
            />
          </Stack>
          <ConfigDisplayPortsControlToggle
            config={config}
            isDisabled={isDisplaySettingsUpdateDisabled}
          />
        </Stack>
      ) : (
        <>{t('lockingNotSupported')}</>
      )}
    </GridItem>
  );
}

ConfigDisplayLockingSection.graphql = {
  fragments: {
    ConfigDisplayLockingSection_config: gql`
      fragment ConfigDisplayLockingSection_config on ConfigurationModel {
        id
        ...ConfigDisplayInfraRedControlToggle_config
        ...ConfigDisplayKeyboardControlToggle_config
        ...ConfigDisplayPortsControlToggle_config
      }
    `,
  },
};
